import cx from "classnames";
import { useCallback, useMemo, useState } from "react";
import { t } from "ttag";

import { ForwardRefLink } from "metabase/common/components/Link";
import { SourceColorIndicator } from "metabase/common/components/SourceColorIndicator";
import type {
  MetricsViewerDefinitionEntry,
  SelectedMetric,
} from "metabase/metrics-viewer/types";
import {
  getDimensionsByType,
  getEntryBreakout,
} from "metabase/metrics-viewer/utils";
import { Box, Flex, Icon, Menu, Pill, Popover, Skeleton } from "metabase/ui";
import * as Urls from "metabase/urls";
import type { ProjectionClause } from "metabase-lib/metric";
import * as LibMetric from "metabase-lib/metric";

import { BreakoutDimensionPicker } from "../../BreakoutDimensionPicker";
import { MetricSearchDropdown } from "../MetricSearchDropdown";

import S from "./MetricPill.module.css";

type PillPopoverState = "closed" | "menu" | "swap" | "breakout-picker";

type MetricPillProps = {
  metric: SelectedMetric;
  colors?: string[];
  definitionEntry: MetricsViewerDefinitionEntry;
  isDisabled?: boolean;
  onSwap: (oldMetric: SelectedMetric, newMetric: SelectedMetric) => void;
  onRemove: (metricId: number, sourceType: "metric" | "measure") => void;
  onSetBreakout: (dimension: ProjectionClause | undefined) => void;
  onOpen?: () => void;
};

export function MetricPill({
  metric,
  colors,
  definitionEntry,
  isDisabled,
  onSwap,
  onRemove,
  onSetBreakout,
  onOpen,
}: MetricPillProps) {
  const [popoverState, setPopoverState] = useState<PillPopoverState>("closed");

  const dimensions = useMemo(
    () =>
      definitionEntry.definition
        ? getDimensionsByType(definitionEntry.definition)
        : new Map(),
    [definitionEntry.definition],
  );

  const { definition } = definitionEntry;

  const breakoutDimension = useMemo(
    () => getEntryBreakout(definitionEntry),
    [definitionEntry],
  );

  const breakoutDimensionName = useMemo(() => {
    if (!breakoutDimension || !definition) {
      return null;
    }
    const rawDim = LibMetric.projectionDimension(definition, breakoutDimension);
    if (!rawDim) {
      return null;
    }
    return LibMetric.displayInfo(definition, rawDim).name ?? null;
  }, [breakoutDimension, definition]);

  const handleSelect = useCallback(
    (newMetric: SelectedMetric) => {
      onSwap(metric, newMetric);
      setPopoverState("closed");
    },
    [metric, onSwap],
  );

  const handleClose = useCallback(() => {
    setPopoverState("closed");
  }, []);

  const handleOpen = useCallback(() => {
    onOpen?.();
    setPopoverState("menu");
  }, [onOpen]);

  const handleOpenReplace = useCallback(() => {
    setPopoverState("swap");
  }, []);

  const handleOpenBreakoutPicker = useCallback(() => {
    setPopoverState("breakout-picker");
  }, []);

  const handleRemoveBreakout = useCallback(() => {
    onSetBreakout(undefined);
    setPopoverState("closed");
  }, [onSetBreakout]);

  return (
    <Box component="span" pos="relative" display="inline-flex">
      <Popover
        opened={popoverState === "swap"}
        onChange={(opened) => {
          if (!opened) {
            setPopoverState("closed");
          }
        }}
        position="bottom-start"
        shadow="md"
        withinPortal
        trapFocus
        // these are the defaults, but listing them explicitly
        // since we're going to add listeners to stop propagation
        clickOutsideEvents={["mousedown", "touchstart"]}
      >
        <Popover.Target>
          <Pill
            className={S.metricPill}
            c="text-primary"
            h="2rem"
            px="sm"
            py="xs"
            fw={600}
            withRemoveButton
            onRemove={() => {
              onRemove(metric.id, metric.sourceType);
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            removeButtonProps={{
              mr: 0,
              "aria-label": metric.name
                ? t`Remove ${metric.name}`
                : t`Remove metric`,
            }}
            data-testid="metrics-viewer-pill"
          >
            <Flex align="center" gap="xs">
              {metric.isLoading ? (
                <Skeleton mt="xs" w="4.5rem" h="1rem" />
              ) : (
                <>
                  <SourceColorIndicator
                    colors={
                      isDisabled ? ["var(--mb-color-icon-secondary)"] : colors
                    }
                    fallbackIcon={
                      metric.sourceType === "measure" ? "ruler" : "metric"
                    }
                  />
                  <span
                    className={cx(S.metricPillText, isDisabled && S.disabled)}
                  >
                    {metric.name}
                  </span>
                </>
              )}
            </Flex>
          </Pill>
        </Popover.Target>
        <Popover.Dropdown
          p={0}
          mt="-0.15rem"
          bd="none" // the border causes the minipicker to be slightly mispositioned
        >
          <div
            // prevent clicks in mini or entity picker from closing the popover
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <MetricSearchDropdown
              onSelect={handleSelect}
              onClose={handleClose}
              selectedMetric={metric}
              menuProps={{
                offset: 0,
              }}
            />
          </div>
        </Popover.Dropdown>
      </Popover>
      <Menu
        opened={popoverState === "menu"}
        onChange={(opened) => {
          if (!opened) {
            setPopoverState((prev) => (prev === "menu" ? "closed" : prev));
          }
        }}
        position="bottom-start"
        withinPortal
      >
        <Menu.Target>
          <Box
            component="span"
            pos="absolute"
            inset={0}
            className={S.menuTarget}
          />
        </Menu.Target>
        <Menu.Dropdown mt="-0.15rem">
          <Menu.Item
            leftSection={<Icon name="sync" />}
            onClick={handleOpenReplace}
          >
            {t`Replace`}
          </Menu.Item>
          {dimensions.size > 0 && definition && (
            <>
              {breakoutDimension && (
                <Menu.Item
                  leftSection={<Icon name="close" />}
                  onClick={handleRemoveBreakout}
                >
                  {t`Remove series breakout`}
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<Icon name="arrow_split" />}
                onClick={handleOpenBreakoutPicker}
              >
                {breakoutDimension
                  ? t`Change series breakout`
                  : t`Add a series breakout`}
              </Menu.Item>
            </>
          )}
          {metric.sourceType === "metric" && (
            <Menu.Item
              leftSection={<Icon name="home" />}
              component={ForwardRefLink}
              to={Urls.metricQuestionUrl({
                id: metric.id,
                name: metric.name ?? undefined,
              })}
            >
              {t`Go to metric home page`}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      {definition && (
        <Popover
          opened={popoverState === "breakout-picker"}
          onChange={(opened) => {
            if (!opened) {
              setPopoverState("closed");
            }
          }}
          position="bottom-start"
          shadow="md"
          withinPortal
        >
          <Popover.Target>
            <Box
              component="span"
              pos="absolute"
              inset={0}
              className={S.menuTarget}
            />
          </Popover.Target>
          <Popover.Dropdown p={0} mt="-0.15rem">
            <BreakoutDimensionPicker
              definition={definition}
              currentBreakoutDimension={breakoutDimension}
              currentBreakoutDimensionName={breakoutDimensionName}
              onSelect={onSetBreakout}
              onClose={handleClose}
            />
          </Popover.Dropdown>
        </Popover>
      )}
    </Box>
  );
}
